import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEnGb from '@angular/common/locales/en-GB';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  Router,
  TitleStrategy,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { provideNgIconsConfig } from '@ng-icons/core';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { provideMatomo, withRouteData, withRouter } from 'ngx-matomo-client';

import version from '@optical/version';

import { APP_URL, APP_VERSION, AuthStore } from '@webapp/shared';

import { environment } from '../environments/environment';
import { ApiHttpInterceptorFn } from './interceptors/api.interceptor';
import { AuthHttpInterceptorFn } from './interceptors/auth.interceptor';
import { TeamIdHttpInterceptorFn } from './interceptors/team-id.interceptor';
import { APP_ROUTES, TranslatedTitleStrategy } from './routes';

registerLocaleData(localeDe);
registerLocaleData(localeEn);
registerLocaleData(localeEnGb);

export const appConfig: ApplicationConfig = {
  providers: [
    // Use `importProvidersFrom` for libraries that only offer an NgModule API for their DI configuration
    importProvidersFrom(BrowserModule),
    // This utility configures the Angular CDK overlay to disable the `usePopover`
    // behavior introduced in Angular 21, which causes CDK overlay-based components
    // (sheets, dialogs, tooltips, etc.) to render above `position: fixed` elements.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    provideHttpClient(
      withInterceptors([ApiHttpInterceptorFn, AuthHttpInterceptorFn, TeamIdHttpInterceptorFn]),
    ),
    provideMatomo(
      {
        disabled: !environment.production,
        siteId: environment.matomo.siteId,
        trackerUrl: environment.matomo.trackerUrl,
      },
      withRouter(),
      withRouteData(),
    ),
    provideAppInitializer(() => {
      // Returning the promise blocks the navigation pipeline until the session is resolved.
      return inject(AuthStore).refreshAuthSession();
    }),
    provideRouter(
      APP_ROUTES,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
      }),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
    ),
    provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        enforceLoading: true,
        prefix: './i18n/',
        suffix: '.json',
      }),
    }),
    {
      provide: TitleStrategy,
      useClass: TranslatedTitleStrategy,
    },
    {
      provide: APP_URL,
      useValue: environment.appUrl,
    },
    {
      provide: APP_VERSION,
      useValue: version,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideNgIconsConfig({
      strokeWidth: 2,
    }),
  ],
};
